// src/components/Navbar.js
//
// Top navigation. Main pages sit in the bar; the logbook's supporting pages
// (aircraft, crew, search, import/export, CrewAccess) are under "Logbook";
// profile, settings, admin and sign out are under the user's name. On small
// screens everything folds into one menu with the same groups.
//
// Menus close on a click outside, on Escape and on navigation, and only one
// is open at a time. A section stays highlighted on its sub-pages (editing a
// flight highlights Flight Log, editing an aircraft highlights Logbook).

import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useRegistrationStatus from '../hooks/useRegistrationStatus';
import {
  Plane,
  Menu,
  X,
  User,
  LogOut,
  Settings,
  Shield,
  ChevronDown,
  LogIn,
  Loader2,
  BarChart3,
  Users,
  Search,
  Plus,
  ArrowRightFromLine,
  LayoutDashboard,
  BookOpen,
  Gauge,
  DownloadCloud,
  FolderOpen,
} from 'lucide-react';
import { useDialog } from '../contexts/DialogContext';

// `match` lists the paths, or path prefixes ending in "/", that count as
// being on that page
const MAIN_NAV = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, match: ['/'] },
  { name: 'Flight Log', href: '/flights', icon: BookOpen, match: ['/flights', '/edit-flight/'] },
  { name: 'Statistics', href: '/statistics', icon: BarChart3, match: ['/statistics'] },
  { name: 'FAR 117', href: '/far117', icon: Gauge, match: ['/far117'] },
];

// Groups in the Logbook menu, separated by a divider
const LOGBOOK_NAV = [
  [
    { name: 'Aircraft', href: '/aircraft', icon: Plane, match: ['/aircraft', '/add-aircraft', '/edit-aircraft/'] },
    { name: 'Crew', href: '/crew', icon: Users, match: ['/crew', '/add-crew', '/edit-crew/'] },
    { name: 'Search Flights', href: '/search', icon: Search, match: ['/search'] },
  ],
  [
    { name: 'Import/Export', href: '/data', icon: ArrowRightFromLine, match: ['/data'] },
    { name: 'CrewAccess', href: '/crewaccess', icon: DownloadCloud, match: ['/crewaccess'] },
  ],
];

const ACCOUNT_NAV = [
  { name: 'Profile', href: '/profile', icon: User, match: ['/profile'] },
  { name: 'Settings', href: '/settings', icon: Settings, match: ['/settings'] },
];

const ADMIN_NAV = { name: 'User Management', href: '/admin/users', icon: Shield, match: ['/admin/'] };

const matches = (item, pathname) => item.match.some(m =>
  m.endsWith('/') && m !== '/' ? pathname.startsWith(m) : pathname === m);

const barLink = (active) => `flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
  active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
}`;

const menuLink = (active) => `flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
  active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
}`;

const mobileLink = (active) => `flex items-center gap-2 px-3 py-2 rounded-md text-base font-medium ${
  active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
}`;

const fullName = (user) => [user?.firstName, user?.lastName].filter(Boolean).join(' ');

const Navbar = () => {
  const { confirm } = useDialog();
  const [mobileOpen, setMobileOpen] = useState(false);
  // 'logbook' | 'user' | null — only one dropdown open at a time
  const [openMenu, setOpenMenu] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const registrationEnabled = useRegistrationStatus();

  // Close menus on navigation (including back/forward)
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Close menus on a click outside the bar, or Escape
  useEffect(() => {
    if (!openMenu && !mobileOpen) return undefined;
    const close = () => {
      setOpenMenu(null);
      setMobileOpen(false);
    };
    const onPointerDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) close();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenu, mobileOpen]);

  const toggleMenu = (name) => setOpenMenu(current => (current === name ? null : name));

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setOpenMenu(null);
      setMobileOpen(false);
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const confirmLogout = async () => {
    if (await confirm({ title: 'Sign out?', message: 'You\'ll need to log in again to view your logbook.', confirmLabel: 'Sign out' })) {
      handleLogout();
    }
  };

  const path = location.pathname;
  const signedIn = isAuthenticated();
  const accountItems = signedIn && isAdmin() ? [...ACCOUNT_NAV, ADMIN_NAV] : ACCOUNT_NAV;
  const logbookActive = LOGBOOK_NAV.flat().some(item => matches(item, path));

  const navLink = (item, className) => {
    const active = matches(item, path);
    const Icon = item.icon;
    return (
      <Link key={item.href} to={item.href} className={className(active)} aria-current={active ? 'page' : undefined}>
        <Icon className="h-4 w-4 shrink-0" />
        {item.name}
      </Link>
    );
  };

  const signOutButton = (className) => (
    <button
      type="button"
      onClick={confirmLogout}
      disabled={isLoggingOut}
      className={`${className} w-full text-left disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {isLoggingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
      {isLoggingOut ? 'Signing out…' : 'Sign out'}
    </button>
  );

  return (
    <nav ref={navRef} className="bg-white shadow-lg fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 gap-4">
          {/* Brand */}
          <Link to="/" className="flex items-center shrink-0">
            <Plane className="h-8 w-8 text-blue-600" />
            <span className="ml-2 text-xl font-bold text-gray-900">Compflightlog</span>
          </Link>

          {/* Desktop */}
          {signedIn ? (
            <div className="hidden lg:flex items-center gap-1 flex-1">
              {MAIN_NAV.map(item => navLink(item, barLink))}

              <div className="relative">
                <button
                  type="button"
                  onClick={() => toggleMenu('logbook')}
                  aria-haspopup="menu"
                  aria-expanded={openMenu === 'logbook'}
                  className={barLink(logbookActive)}
                >
                  <FolderOpen className="h-4 w-4" />
                  Logbook
                  <ChevronDown className={`h-4 w-4 transition-transform ${openMenu === 'logbook' ? 'rotate-180' : ''}`} />
                </button>
                {openMenu === 'logbook' && (
                  <div role="menu" className="absolute left-0 mt-2 w-52 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
                    {LOGBOOK_NAV.map((group, i) => (
                      <div key={group[0].href} className={i > 0 ? 'border-t border-gray-100 mt-1 pt-1' : undefined}>
                        {group.map(item => navLink(item, menuLink))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex-1" />

              <Link
                to="/add-flight"
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Flight
              </Link>

              <div className="relative ml-2">
                <button
                  type="button"
                  onClick={() => toggleMenu('user')}
                  disabled={isLoggingOut}
                  aria-haspopup="menu"
                  aria-expanded={openMenu === 'user'}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  <span className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600" />
                  </span>
                  <span className="font-medium text-gray-900">{user?.firstName}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${openMenu === 'user' ? 'rotate-180' : ''}`} />
                </button>
                {openMenu === 'user' && !isLoggingOut && (
                  <div role="menu" className="absolute right-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{fullName(user)}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      {accountItems.map(item => navLink(item, menuLink))}
                    </div>
                    <div className="border-t border-gray-100 pt-1">
                      {signOutButton('flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/about" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">About</Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">Contact</Link>
              <Link
                to="/login"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </Link>
              {registrationEnabled && (
                <Link
                  to="/register"
                  className="flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Register
                </Link>
              )}
            </div>
          )}

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => { setOpenMenu(null); setMobileOpen(open => !open); }}
            disabled={isLoggingOut}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:opacity-50"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && !isLoggingOut && (
        <div className="lg:hidden border-t bg-white max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {signedIn ? (
              <>
                <Link
                  to="/add-flight"
                  className="flex items-center justify-center gap-2 px-3 py-2 mb-2 rounded-md text-base font-medium bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Plus className="h-5 w-5" />
                  Add Flight
                </Link>
                {MAIN_NAV.map(item => navLink(item, mobileLink))}

                <div className="border-t border-gray-200 pt-2 mt-2">
                  <p className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Logbook</p>
                  {LOGBOOK_NAV.flat().map(item => navLink(item, mobileLink))}
                </div>

                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div className="px-3 py-1">
                    <p className="text-sm font-medium text-gray-900">{fullName(user)}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  {accountItems.map(item => navLink(item, mobileLink))}
                  {signOutButton('flex items-center gap-2 px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100')}
                </div>
              </>
            ) : (
              <>
                <Link to="/about" className={mobileLink(path === '/about')}>About</Link>
                <Link to="/contact" className={mobileLink(path === '/contact')}>Contact</Link>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <Link to="/login" className="flex items-center gap-2 px-3 py-2 rounded-md text-base font-medium text-white bg-blue-600 hover:bg-blue-700">
                    <LogIn className="h-5 w-5" />
                    Sign In
                  </Link>
                  {registrationEnabled && (
                    <Link to="/register" className={`${mobileLink(path === '/register')} mt-1`}>
                      Register
                    </Link>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
